<template>
    <div class="flex flex-col gap-6 w-full">

        <!-- Has Opening Hours Toggle -->
        <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-gray-900 dark:text-white">
                {{ $t('Has Opening Hours') }}
            </label>
            <label class="flex items-center gap-2 cursor-pointer select-none">
                <span class="text-xs text-gray-500 dark:text-neutral-400">{{ hasOpeningHours ? $t('Yes') : $t('No') }}</span>
                <div @click="!disabled && toggleHasOpeningHours()" :class="[
                    'relative w-8 h-4 rounded-full transition-colors',
                    hasOpeningHours ? 'bg-primary' : 'bg-gray-300 dark:bg-neutral-600',
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                ]">
                    <span :class="[
                        'absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform',
                        hasOpeningHours ? 'translate-x-4' : ''
                    ]"></span>
                </div>
            </label>
        </div>
        <template v-if="hasOpeningHours">
            <!-- Opening Hours -->
            <div class="w-full">
                <label class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                    {{ $t('Opening Hours') }}
                </label>

                <div class="rounded-lg border border-gray-300 dark:border-neutral-700 overflow-hidden">
                    <div v-for="(day, index) in openingHours" :key="day.day" :class="[
                        'flex flex-col sm:flex-row sm:items-center gap-3 p-3 text-sm',
                        index !== openingHours.length - 1 ? 'border-b border-gray-200 dark:border-neutral-700' : ''
                    ]">
                        <!-- Day name + closed toggle -->
                        <div class="flex items-center justify-between gap-3 shrink-0">
                            <label class="flex items-center gap-1.5 cursor-pointer select-none">
                                <div @click="!disabled && toggleClosed(day)" :class="[
                                    'relative w-8 h-4 rounded-full transition-colors',
                                    day.closed ? 'bg-gray-300 dark:bg-neutral-600' : 'bg-primary',
                                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                                ]">
                                    <span :class="[
                                        'absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform',
                                        day.closed ? '' : 'translate-x-4'
                                    ]"></span>
                                </div>
                                <span class="text-xs text-gray-500 dark:text-neutral-400">{{ day.closed ? $t('Closed')
                                    : $t('Open') }}</span>
                            </label>
                            <span class="font-medium text-gray-700 dark:text-neutral-200 capitalize">{{ $t(day.day) }}</span>
                        </div>

                        <!-- Time inputs -->
                        <div v-if="!day.closed" class="flex items-center gap-2 flex-1 justify-end">
                            <input type="time" v-model="day.open_time" :disabled="disabled" :class="[
                                'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white',
                                disabled ? 'cursor-not-allowed opacity-60' : ''
                            ]" />
                            <span class="text-gray-400 dark:text-neutral-500 text-xs">{{ $t('to') }}</span>
                            <input type="time" v-model="day.close_time" :disabled="disabled" :class="[
                                'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white',
                                disabled ? 'cursor-not-allowed opacity-60' : ''
                            ]" />
                        </div>
                        <div v-else class="flex flex-1 justify-end h-[38.7px] items-center">
                            <span class="block text-xs text-gray-400 text-right dark:text-neutral-500 italic">{{ $t('Closed all day') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Special Opening Hours -->
            <div class="w-full">
                <div class="flex items-center justify-between mb-1">
                    <label class="block text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t('Special Opening Hours') }}
                    </label>
                    <button v-if="!disabled" type="button" @click="addSpecialDay"
                        class="text-xs text-primary hover:underline font-medium">
                        + {{ $t('Add day') }}
                    </button>
                </div>

                <div v-if="specialOpeningHours.length === 0" class="text-sm text-gray-400 dark:text-neutral-500 italic px-1">
                    {{ $t('No special days added yet') }}.
                </div>


                <div v-else class="rounded-lg border border-gray-300 dark:border-neutral-700 overflow-hidden">
                    <div v-for="(special, index) in specialOpeningHours" :key="index" :class="[
                        'flex flex-col sm:flex-row sm:items-center gap-3 p-3 text-sm',
                        index !== specialOpeningHours.length - 1 ? 'border-b border-gray-200 dark:border-neutral-700' : ''
                    ]">
                        <!-- Closed toggle -->
                        <label class="flex items-center gap-1.5 cursor-pointer select-none shrink-0">
                            <div @click="!disabled && (special.is_closed = !special.is_closed)" :class="[
                                'relative w-8 h-4 rounded-full transition-colors',
                                special.is_closed ? 'bg-gray-300 dark:bg-neutral-600'  :  'bg-primary',
                                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            ]">
                                <span :class="[
                                    'absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform',
                                    special.is_closed ? '' : 'translate-x-4' 
                                ]"></span>
                            </div>
                            <span class="text-xs text-gray-500 dark:text-neutral-400">{{ special.is_closed ? $t('Closed') :
                                $t('Open') }}</span>
                        </label>

                        <!-- Label input -->
                        <input type="text" v-model="special.description" :placeholder="$t('e.g. Christmas Day')"
                            :disabled="disabled" :class="[
                                'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 w-full sm:w-40 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-neutral-400',
                                disabled ? 'cursor-not-allowed opacity-60' : ''
                            ]" />

                        <!-- Date input -->
                        <input type="date" v-model="special.date" :disabled="disabled" :class="[
                            'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white',
                            disabled ? 'cursor-not-allowed opacity-60' : ''
                        ]" />

                        <!-- Time inputs -->
                        <div v-if="!special.is_closed" class="flex items-center gap-2 ml-auto">
                            <input type="time" v-model="special.open_time" :disabled="disabled" :class="[
                                'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white',
                                disabled ? 'cursor-not-allowed opacity-60' : ''
                            ]" />
                            <span class="text-gray-400 dark:text-neutral-500 text-xs">{{ $t('to') }}</span>
                            <input type="time" v-model="special.close_time" :disabled="disabled" :class="[
                                'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white',
                                disabled ? 'cursor-not-allowed opacity-60' : ''
                            ]" />
                        </div>

                        <!-- Remove button -->
                        <button v-if="!disabled" type="button" @click="removeSpecialDay(index)"
                            class="text-gray-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400 transition-colors shrink-0"
                            :title="$t('Remove')">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- Hidden inputs for form submission -->
        <input type="hidden" name="opening_hours" :value="JSON.stringify(openingHours)" />
        <input type="hidden" name="special_opening_hours" :value="JSON.stringify(specialOpeningHours)" />
    </div>
</template>

<script>
export default {
    name: 'OpeningHours',

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        initialOpeningHours: {
            type: Array,
            default: null,
        },
        initialSpecialOpeningHours: {
            type: Array,
            default: () => [],
        },
        initHasOpeningHours: {
            type: Boolean,
            default: true,
        }
    },

    data() {
        const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        const merged = allDays.map(day => {
            const existing = (this.initialOpeningHours ?? []).find(h => h.day == day);
            return existing
                ? {
                    ...existing, closed: true
                }
                : { id: null, uuid: null, day, open_time: '09:00', close_time: '17:00', closed: false };
        });

        return {
            hasOpeningHours: this.initHasOpeningHours,
            openingHours: merged,
            specialOpeningHours: this.initialSpecialOpeningHours ?? [],
            _initializing: false,
        };
    },

    methods: {
        toggleHasOpeningHours() {
            this.hasOpeningHours = !this.hasOpeningHours;
            this.$emit('update:hasOpeningHours', this.hasOpeningHours);
            if (!this.hasOpeningHours) {
                // Reset to defaults when toggled off
                const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                this.openingHours = allDays.map(day => ({
                    id: null, uuid: null, day, open_time: '09:00', close_time: '17:00', closed: true,
                }));
                this.specialOpeningHours = [];
                this.$emit('update:openingHours', []);
                this.$emit('update:specialOpeningHours', []);
            } else {
                const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

                console.log(this.initialOpeningHours);
                const merged = allDays.map(day => {
                    const existing = (this.initialOpeningHours ?? []).find(h => h.day == day);
                    return existing
                        ? {
                            ...existing, closed: false
                        }
                        : { id: null, uuid: null, day, open_time: '09:00', close_time: '17:00', closed: true };
                });
                console.log(merged);
                this.openingHours = merged;
                this.specialOpeningHours = this.initialSpecialOpeningHours ?? [];

                this.$emit('update:openingHours', this.openingHours);
                this.$emit('update:specialOpeningHours', this.specialOpeningHours);
            }
        },
        toggleClosed(day) {
            day.closed = !day.closed;
        },
        addSpecialDay() {
            this.specialOpeningHours.push({
                id: null,
                uuid: null,
                description: '',
                date: '',
                is_closed: false,
                open_time: '09:00',
                close_time: '17:00',
            });
        },
        removeSpecialDay(index) {
            this.specialOpeningHours.splice(index, 1);
        },
    },

    watch: {
        initialOpeningHours(newVal) {
            if (!newVal?.length) return;
            this._initializing = true;
            const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
            this.openingHours = allDays.map(day => {
                const existing = newVal.find(h => h.day === day);
                if (existing) {
                    return { ...existing, open_time: existing.open_time?.slice(0, 5), close_time: existing.close_time?.slice(0, 5), closed: false };
                }
                return { id: null, uuid: null, day, open_time: '09:00', close_time: '17:00', closed: true };
            });
            this.$nextTick(() => { this._initializing = false; });
        },
        initialSpecialOpeningHours(newVal) {
            if (!newVal) return;
            this._initializing = true;
            this.specialOpeningHours = newVal.map(s => ({
                ...s,
                date: s.date?.slice(0, 10),
                open_time: s.open_time?.slice(0, 5),
                close_time: s.close_time?.slice(0, 5),
                description: s.description.en,
                is_closed: Boolean(s.is_closed),
            }));

            this.$nextTick(() => { this._initializing = false; });
        },
        initHasOpeningHours(newVal) {
            this.hasOpeningHours = newVal;
        },
        openingHours: {
            deep: true,
            handler(val) {
                this.$emit('update:openingHours', val.filter(d => !d.closed));
            }
        },
        specialOpeningHours: {
            deep: true,
            handler(val) {
                this.$emit('update:specialOpeningHours', val);
            }
        },
    },
};
</script>